import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Agentless, protocol-native',
    description:
      'Connect to RDP, SSH, VNC, web apps, and databases without installing anything on the target host. The gateway speaks each protocol natively.',
    bullets: [
      'RDP and Windows Remote Apps',
      'SSH, SCP, SFTP, and Telnet',
      'VNC, X11, and PowerShell / WinRM',
      'Kubernetes, web apps, and databases (PostgreSQL, MySQL, MSSQL, Oracle)',
    ],
  },
  {
    title: 'Browser-based, no client to install',
    description:
      'Privileged users open a browser tab and they are inside the target system. No fat client, no jump-box hopping, no VPN.',
    bullets: [
      'HTML5 RDP / SSH / VNC clients',
      'Native client passthrough when needed',
      'Copy-paste, file transfer, and clipboard policy',
      'Multi-monitor and high-DPI support',
    ],
  },
  {
    title: 'Network-segmentation friendly',
    description:
      'Deploy the gateway in a DMZ or air-gapped segment. Users access protected zones without flat-network exposure.',
    bullets: [
      'DMZ and reverse-tunnel deployment modes',
      'Multi-gateway clustering for high availability',
      'Per-zone gateway placement',
      'No inbound connections to protected networks',
    ],
  },
  {
    title: 'Policy on the wire',
    description:
      'Enforce session-time limits, command filters, and data-transfer policies at the gateway, before traffic ever reaches the target host.',
    bullets: [
      'Command-allow / -deny lists for SSH',
      'Clipboard and file-transfer policy',
      'Session time-limit enforcement',
      'Source-IP and device-posture checks',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Drop in the gateway',
    description:
      'Deploy as a container, virtual appliance, or bare-metal install. Cluster for HA. No agents anywhere else.',
  },
  {
    title: 'Map the assets',
    description:
      'Register the systems, group them by environment, and tie each one to vault credentials and approval policy.',
  },
  {
    title: 'Open a session',
    description:
      'Users hit the portal, the gateway brokers the protocol, and every keystroke is recorded as it flows through.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Privileged vault',
    description:
      'Credentials the gateway injects at session start.',
    href: '/pam/vault',
  },
  {
    label: 'Session recording',
    description:
      'Video and keystroke capture for every brokered session.',
    href: '/pam/session-recording',
  },
  {
    label: 'Use case: DevOps privileged access',
    description:
      'Clusters, databases, and servers: one brokered model.',
    href: '/solutions/devops-privileged-access',
  },
]

export default function GatewayPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Monopam Gateway: agentless privileged access"
        description="Browser-based, agentless gateway for RDP, SSH, Telnet, VNC, Kubernetes, web, and database access. Brokers credentials, records sessions, enforces policy on the wire."
        path="/pam/gateway"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Gateway"
          title={
            <>
              Privileged access,
              <br />
              from a browser tab.
            </>
          }
          body="Agentless gateway for RDP, SSH, VNC, web, and database access. No fat client, no jump-box maze, no agents on target hosts: just a brokered session with policy enforced on the wire."
          badges={[
            'Agentless on target hosts',
            'Browser-based clients',
            'HA-ready clustering',
          ]}
        />
        <SolutionPillars
          title={<>One gateway. Every protocol. Zero agents on prod.</>}
          body="Monopam Gateway is the front door to your most sensitive systems. It speaks the protocols natively, injects credentials at runtime, and records everything in between."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Drop in. Map assets. Open a session.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
